import { RouteProp, useRoute } from "@react-navigation/native";
import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import AutoRenewalConsentSection from "components/AutoRenewalConsentSection";
import FullScreenSpinner from "components/FullScreenSpinner";
import MainContainer from "components/MainContainer";
import Stepper, { StepperContent } from "components/Stepper/Stepper";
import dayjs from "dayjs";
import { resetAddMembershipFlow } from "features/slices/addMembershipFlowSlice";
import {
  fetchAddMemberPlanPackages,
  fetchSignupCharge,
  resetBuyFlow,
  setSelectedPlanPackage,
} from "features/slices/buyFlowSlice";
import { PlanPackageUserFieldDetails, UserFields } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useSendConversionData from "hooks/useSendConversionData";
import { differenceBy } from "lodash";
import { ManageSubscriptionsStackParamList } from "navigation/types";
import i18n from "plugins/localization";
import { addSubscriptionFetcher } from "queries/account.queries";
import { useFetchAgreementsQuery } from "queries/agreement.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { updateUserFetcher } from "queries/user.queries";
import React, { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import StepThree from "screens/AddMembership/StepThree";
import { useDebounce } from "use-debounce";
import { toManageMemberSubscription } from "utils/navigationUtils";
import { getPlanType } from "utils/planPackageUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import toast from "utils/toast";
import StepOne from "./StepOne";

const AddNewPlan = () => {
  const { params } =
    useRoute<
      RouteProp<
        ManageSubscriptionsStackParamList,
        "AddNewPlanWithPlanPackageId"
      >
    >();

  const [quantity, setQuantity] = useState(1);
  const [debouncedQuantity] = useDebounce(quantity, 250);
  const dispatch = useAppDispatch();
  const { account, updateFetchAccountBffCache } = useAccount();
  const {
    selectedPlanPackage,
    planPackages,
    selectedBillingInterval,
    step,
    signupCharge,
  } = useAppSelector((state) => state.buyFlow);

  const sendConversionData = useSendConversionData();

  const [autoRenewalConsentGiven, setAutoRenewalConsentGiven] = useState(false);
  const { data: autoRenewalAgreements } = useFetchAgreementsQuery(
    "AUTO_RENEWAL",
    selectedPlanPackage?.id,
    selectedBillingInterval,
  );
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { data: tenantBranding } = useFetchTenantBrandingQuery();
  const isAutoRenewPlan =
    selectedPlanPackage?.requireAutoRenewalConsent === true;

  const { mutateAsync: updateUser } = useMutation(updateUserFetcher);
  const { mutateAsync: addSubscription, isLoading: loading } = useMutation(
    addSubscriptionFetcher,
  );

  const currentMember = useMemo(() => {
    if (!account) return null;
    return account.members.find(
      (member) => member.id === Number(params?.memberId),
    );
  }, [account]);

  const currentMemberReservationPlans = useMemo(() => {
    if (!account || !currentMember) return [];

    return (
      account?.subscriptions
        .filter(
          (sub) =>
            sub.member &&
            sub.member.id === currentMember.id &&
            filterIneligibleSubscriptions(sub) &&
            !sub.child,
        )
        .filter((sub) => getPlanType(sub.planPackage) === "RESERVATION")
        .map((sub) => sub.planPackage) || []
    );
  }, [account, currentMember]);

  const existingPlanCount = useMemo(() => {
    if (!account || !currentMember || !selectedPlanPackage) return 0;
    return account.subscriptions.filter(
      (sub) =>
        sub.member &&
        sub.member.id === currentMember.id &&
        sub.planPackage.id === selectedPlanPackage.id &&
        filterIneligibleSubscriptions(sub),
    ).length;
  }, [account, currentMember, selectedPlanPackage]);

  const { planPackageUserFields } = usePlanPackageUserFields(
    selectedPlanPackage?.id,
  );
  const filteredPlanPackages = differenceBy(
    planPackages,
    currentMemberReservationPlans,
    "id",
  );
  const convertUserFieldsToCorrectFormat = (
    userFields: UserFields,
    planPackageUserFields: PlanPackageUserFieldDetails[],
  ) => {
    planPackageUserFields.forEach(({ field }) => {
      if (field.type === "DATE") {
        userFields[field.name] = dayjs(userFields[field.name]).format(
          "YYYY-MM-DD",
        );
      } else if (field.type === "DATETIME") {
        userFields[field.name] = dayjs(userFields[field.name]).format(
          "YYYY-MM-DD HH:mm:ss",
        );
      }
    });
    return userFields;
  };

  const addUserFieldsToUser = async (userFields: UserFields) => {
    if (userFields && Object.keys(userFields).length > 0) {
      await updateUser({
        userId: currentMember!.user.id,
        user: {
          ...currentMember!.user,
          ...userFields,
          userFields: {
            ...currentMember!.user.userFields,
            ...convertUserFieldsToCorrectFormat(
              userFields,
              planPackageUserFields!,
            ),
          },
        },
      });
    }
  };

  const addSubscriptionToAccount = async (userFields?: UserFields) => {
    if (userFields) {
      await addUserFieldsToUser(userFields);
    }
    const updatedAccount = await addSubscription({
      billingInterval: selectedBillingInterval,
      userInfo: [
        {
          userId: currentMember!.user.id,
          planPackageIds: new Array(debouncedQuantity).fill(
            selectedPlanPackage!.id,
          ),
        },
      ],
      accountId: account!.id,
      source: "BUY_FLOW",
      autoRenewalAgreementId:
        isAutoRenewPlan && autoRenewalConsentGiven
          ? autoRenewalAgreements?.[0]?.id
          : undefined,
    });
    toManageMemberSubscription(String(currentMember!.id));
    updateFetchAccountBffCache(updatedAccount);
    dispatch(resetBuyFlow());
    dispatch(resetAddMembershipFlow());
    toast.success(i18n.t("screens.SubscriptionDetail.planAdded"));
    sendConversionData(signupCharge, 1);
  };

  // fetch available plan packages offered by tenant, if already initialized, skip
  useEffect(() => {
    if ((planPackages && planPackages.length > 0) || !account) return;

    dispatch(fetchAddMemberPlanPackages(account));
  }, []);

  // initialize selected plan package
  useEffect(() => {
    if (filteredPlanPackages.length === 0) return;
    if (params?.planPackageId) {
      const predefinedPlanPackage = filteredPlanPackages.find(
        (p) => p.id === Number(params.planPackageId),
      );
      if (predefinedPlanPackage) {
        dispatch(setSelectedPlanPackage(predefinedPlanPackage));
      } else {
        dispatch(setSelectedPlanPackage(filteredPlanPackages[0]));
      }
    }
  }, [planPackages]);

  useEffect(() => {
    if (!selectedPlanPackage) return;
    dispatch(
      fetchSignupCharge({
        billingInterval: selectedBillingInterval,
        planPackageIds: new Array(debouncedQuantity).fill(
          selectedPlanPackage.id,
        ),
      }),
    );
    setAutoRenewalConsentGiven(false);
  }, [selectedPlanPackage, selectedBillingInterval, debouncedQuantity]);

  if (!account) return null;

  if (!planPackages) {
    return (
      <Text category="h3" style={{ textAlign: "center" }}>
        Loading...
      </Text>
    );
  }

  if (loading)
    return (
      <FullScreenSpinner text="Adding Plan to Account"></FullScreenSpinner>
    );

  if (planPackages.length === 0 || !selectedPlanPackage) {
    return (
      <Text category="h3" style={{ textAlign: "center" }}>
        No Additional Plans Available
      </Text>
    );
  }

  return (
    <MainContainer>
      <View className="p-4">
        <Stepper
          step={step}
          steps={[{ name: "Choose Plan" }, { name: "Review" }]}
        >
          <StepperContent>
            <StepOne
              planPackages={planPackages}
              filteredPlanPackages={filteredPlanPackages}
              quantity={quantity}
              setQuantity={setQuantity}
              existingPlanCount={existingPlanCount}
            />
          </StepperContent>
          {/* not showing StepTwo component becuase in the add plans flow, we don't want user to add a new member */}
          <StepperContent>
            {signupCharge && (
              <StepThree
                onSubmit={addSubscriptionToAccount}
                disabled={
                  isAutoRenewPlan &&
                  !!autoRenewalAgreements?.length &&
                  !autoRenewalConsentGiven
                }
                beforeButton={
                  isAutoRenewPlan ? (
                    <AutoRenewalConsentSection
                      agreements={autoRenewalAgreements}
                      consentGiven={autoRenewalConsentGiven}
                      onChange={setAutoRenewalConsentGiven}
                    />
                  ) : undefined
                }
              />
            )}
          </StepperContent>
        </Stepper>
      </View>
    </MainContainer>
  );
};

export default AddNewPlan;
